<template>
  <UiComponentWrapper class="page">
    <!-- Background > Gradient -->
    <UiComponentWrapper class="gradient" />

    <!-- Section > Hero -->
    <UiComponentContainer class="jumbotron">
      <UiComponentWrapper class="content">
        <OrganismHero
          alignment="left"
          :primary-button="
            props.page.isAnAddOn
              ? {
                ...requestDemoCallToAction,
                label: 'Header section CTA',
                slot: props.componentGlobal.contactSales,
              }
              : {
                ...signupOrUpgradeCallToAction,
                label: 'Header section CTA',
              }
          "
          :secondary-button="
            props.page.isAnAddOn
              ? undefined
              : {
                ...requestDemoCallToAction,
                label: 'Header section CTA',
              }
          "
        >
          <template #tag>
            {{ props.page.heroTag }}
          </template>
          <template #heading>
            {{ props.page.heroTitle }}
          </template>
          <template #paragraph>
            {{ props.page.heroText }}
          </template>
        </OrganismHero>
        <UiComponentWrapper class="review">
          <UiComponentWrapper
            class="rating"
            :data-hover="props.componentCustomersReviewLogo.hoverText"
          >
            <UiComponentWrapper class="stars">
              <img
                v-for="n in 4"
                :key="n"
                alt="star"
                height="20"
                loading="lazy"
                src="@/assets/svg/star.svg"
                width="20"
              >
              <img
                alt="star"
                height="20"
                loading="lazy"
                src="@/assets/svg/star-not-complete.svg"
                width="20"
              >
            </UiComponentWrapper>
            <div
              class="copy"
              v-html="`${props.componentHeroReview.from} ${props.componentHeroReview.numberOfReviews}`"
            />
          </UiComponentWrapper>
          <UiComponentWrapper
            v-if="heroLogos.length > 0"
            class="customers"
          >
            <UiComponentWrapper
              v-for="{ id, logo, name } in heroLogos"
              :key="id"
              class="logo"
            >
              <UiComponentImage
                :alt="name"
                contains
                :expected-size="48"
                :extra-params="{
                  con: 100,
                  gam: 100,
                  monochrome: '000000',
                  sat: -100,
                  vib: 100,
                }"
                :height="logo.height"
                :orientation="
                  logo.width > logo.height ? 'portrait' : 'landscape'
                "
                :source="logo.url"
                use-natural-width
                :width="logo.width"
              />
            </UiComponentWrapper>
          </UiComponentWrapper>
        </UiComponentWrapper>
      </UiComponentWrapper>
      <UiComponentWrapper class="animation">
        <UiComponentImage
          v-if="props.page.heroImage"
          :key="`hero-image-${heroImageWidth}`"
          :alt="props.page.heroImage.alt"
          :class="{ 'has-shadow': props.page.heroImage.format === 'jpg' }"
          :expected-size="heroImageWidth"
          :height="props.page.heroImage.height"
          :source="props.page.heroImage.url"
          :width="props.page.heroImage.width"
        />
      </UiComponentWrapper>
    </UiComponentContainer>

    <!-- Section > Benefits -->
    <UiComponentContainer class="benefits">
      <UiComponentWrapper class="inner">
        <UiComponentImage
          v-if="props.page.themeColor.background"
          :key="`${benefitsBackgroundHeight}-${benefitsBackgroundWidth}`"
          :alt="props.page.themeColor.background.alt"
          :expected-size="benefitsBackgroundWidth / 3"
          :extra-params="{
            rect: `${benefitsBackgroundX},${benefitsBackgroundY},${benefitsBackgroundWidth},${benefitsBackgroundHeight}`,
            fit: 'crop',
          }"
          :height="benefitsBackgroundHeight"
          :orientation="
            benefitsBackgroundWidth > benefitsBackgroundHeight
              ? 'landscape'
              : 'portrait'
          "
          :quality="[100, 80, 80]"
          :source="props.page.themeColor.background.url"
          :width="benefitsBackgroundWidth"
        />
        <OrganismHero
          alignment="left"
          :level="2"
          :primary-button="
            props.page.isAnAddOn
              ? {
                ...requestDemoCallToAction,
                label: 'Benefits section CTA',
                slot: props.componentGlobal.contactSales,
              }
              : {
                ...signupOrUpgradeCallToAction,
                label: 'Benefits section CTA',
              }
          "
          :secondary-button="
            props.page.isAnAddOn
              ? undefined
              : {
                ...requestDemoCallToAction,
                label: 'Benefits section CTA',
              }
          "
        >
          <template #tag>
            {{ props.page.benefitsTag }}
          </template>
          <template #heading>
            {{ props.page.benefitsTitle }}
          </template>
          <template #paragraph>
            {{ props.page.benefitsText }}
          </template>
        </OrganismHero>
        <UiComponentWrapper class="list">
          <UiComponentWrapper
            v-for="{ id, text, title } in props.page.benefitsContent"
            :key="id"
            class="item"
          >
            <UiComponentHeading
              as="paragraph"
              :level="4"
            >
              {{ title }}
            </UiComponentHeading>
            <UiComponentParagraph>{{ text }}</UiComponentParagraph>
          </UiComponentWrapper>
        </UiComponentWrapper>
      </UiComponentWrapper>
    </UiComponentContainer>

    <!-- Section > Features -->
    <UiComponentContainer
      v-if="!props.page.isAnAddOn"
      class="features"
    >
      <OrganismHero
        alignment="center"
        :level="2"
        :primary-button="{
          ...signupOrUpgradeCallToAction,
          label: 'Features section CTA',
        }"
        :secondary-button="{
          ...requestDemoCallToAction,
          label: 'Features section CTA',
        }"
      >
        <template #tag>
          {{ props.componentGlobal.features }}
        </template>
        <template #heading>
          {{ props.page.featuresTitle }}
        </template>
        <template #paragraph>
          {{ props.page.featuresDescription }}
        </template>
      </OrganismHero>
      <UiComponentWrapper class="highlighted">
        <UiComponentWrapper
          v-for="(
            { id, title, description, image, theme }, index
          ) in highlightedFeatures"
          :key="`highlighted-features-${id}`"
          class="item"
          :class="theme"
        >
          <UiComponentWrapper class="gradient" />
          <UiComponentHeading :level="3">
            {{ title }}
          </UiComponentHeading>
          <UiComponentParagraph>{{ description }}</UiComponentParagraph>
          <UiComponentImage
            v-if="image"
            :key="`highlighted-feature-image-${
              index % 4 === 0 || index % 4 === 3
                ? highlightedFeatureImageLargeWidth
                : highlightedFeatureImageSmallWidth
            }`"
            :alt="image.alt"
            :class="{ 'has-shadow': image.mimeType !== 'image/png' }"
            :expected-size="
              index % 4 === 0 || index % 4 === 3
                ? highlightedFeatureImageLargeWidth
                : highlightedFeatureImageSmallWidth
            "
            :height="image.height"
            :orientation="image.width > image.height ? 'landscape' : 'portrait'"
            :source="image.url"
            :width="image.width"
          />
        </UiComponentWrapper>
      </UiComponentWrapper>
      <UiComponentWrapper class="list">
        <UiComponentWrapper
          v-for="({ id, cta, features, title }, index) in allFeatures"
          :key="`classic-features-${id}`"
          class="item"
          :class="{ 'has-border': index < allFeatures.length - 1 }"
        >
          <UiComponentWrapper class="head">
            <UiComponentHeading :level="3">
              {{ title }}
            </UiComponentHeading>
            <AtomButton
              v-if="cta"
              :to="cta.url"
              type="text"
            >
              {{ cta.slot }}<UiComponentIcon type="arrow-right" />
            </AtomButton>
          </UiComponentWrapper>

          <UiComponentWrapper class="body">
            <UiComponentWrapper
              v-for="{
                id: featureId,
                enterpriseOnly,
                text: featureText,
                title: featureTitle,
              } in features"
              :key="featureId"
              class="feature"
            >
              <UiComponentHeading
                as="paragraph"
                :level="5"
              >
                {{
                  featureTitle
                }}
              </UiComponentHeading>
              <NuxtLink
                v-if="enterpriseOnly"
                class="enterprise-only"
                :to="localePath('/enterprise')"
              >
                <img
                  alt="Icon"
                  fetchpriority="low"
                  height="12"
                  loading="lazy"
                  src="@/assets/svg/office.svg"
                  width="12"
                >
                {{ props.componentGlobal.enterpriseOnly }}
              </NuxtLink>
              <UiComponentParagraph>{{ featureText }}</UiComponentParagraph>
            </UiComponentWrapper>
          </UiComponentWrapper>

          <UiComponentWrapper class="footer">
            <AtomButton
              v-if="cta"
              :to="cta.url"
              type="text"
            >
              {{ cta.slot }}<UiComponentIcon type="arrow-right" />
            </AtomButton>
          </UiComponentWrapper>
        </UiComponentWrapper>
      </UiComponentWrapper>
    </UiComponentContainer>

    <!-- Section > Related Use Cases -->
    <UiComponentContainer class="use-cases">
      <OrganismHero
        alignment="center"
        :level="2"
      >
        <template #tag>
          {{ props.componentGlobal.useCases }}
        </template>
        <template #heading>
          {{
            props.page.useCaseTitle || props.componentWording.useCaseSectionTitle
          }}
        </template>
      </OrganismHero>
      <UiComponentWrapper class="mobile">
        <UiComponentWrapper
          v-for="{
            id,
            theme,
            icon,
            title,
            hasRichDescription,
            description,
            slug,
          } in relatedUseCases"
          :key="`mobile-${id}`"
          class="use-case"
          :class="theme"
        >
          <UiComponentWrapper class="background">
            <UiComponentIcon
              v-if="icon"
              :type="icon.name"
            />
          </UiComponentWrapper>
          <UiComponentHeading
            as="paragraph"
            :level="3"
          >
            {{ title }}
          </UiComponentHeading>
          <div
            v-if="hasRichDescription"
            class="description"
            v-html="$md.render(description)"
          />
          <UiComponentParagraph v-else>
            {{ description }}
          </UiComponentParagraph>
          <AtomButton
            type="text"
            :to="
              localePath({
                name: 'use-cases-slug',
                params: {
                  slug,
                },
              })
            "
          >
            {{ props.componentGlobal.learnMore }}
            <UiComponentIcon type="arrow-right" />
          </AtomButton>
        </UiComponentWrapper>
      </UiComponentWrapper>
      <UiComponentWrapper class="desktop">
        <UiComponentWrapper class="list">
          <UiComponentWrapper
            v-for="({ id, title }, index) in relatedUseCases"
            :key="`desktop-item-${id}`"
            class="item"
            :class="{
              active: index === relatedUseCasesActiveIndex,
            }"
            @click="relatedUseCasesGlide.go(`=${index}`)"
          >
            <UiComponentParagraph>{{ title }}</UiComponentParagraph>
          </UiComponentWrapper>
        </UiComponentWrapper>
        <div class="slider glide">
          <div
            class="inner glide__track"
            data-glide-el="track"
          >
            <div class="flex glide__slides">
              <div
                v-for="{
                  id,
                  theme,
                  icon,
                  title,
                  hasRichDescription,
                  description,
                  image,
                  slug,
                } in relatedUseCases"
                :key="`desktop-slide-${id}`"
                class="glide__slide swiper-slide use-case"
                :class="theme"
              >
                <UiComponentWrapper class="content">
                  <UiComponentWrapper class="background">
                    <UiComponentIcon
                      v-if="icon"
                      :type="icon.name"
                    />
                  </UiComponentWrapper>
                  <UiComponentHeading :level="3">
                    {{ title }}
                  </UiComponentHeading>
                  <div
                    v-if="hasRichDescription"
                    class="description"
                    v-html="$md.render(description)"
                  />
                  <UiComponentParagraph v-else>
                    {{ description }}
                  </UiComponentParagraph>
                  <AtomButton
                    type="text"
                    :to="
                      localePath({
                        name: 'use-cases-slug',
                        params: {
                          slug,
                        },
                      })
                    "
                  >
                    {{ props.componentGlobal.learnMore }}
                    <UiComponentIcon type="arrow-right" />
                  </AtomButton>
                </UiComponentWrapper>
                <UiComponentImage
                  v-if="image"
                  :alt="image.alt"
                  class="picture"
                  :expected-size="682"
                  :height="image.height"
                  :source="image.url"
                  :width="image.width"
                />
              </div>
            </div>
          </div>
        </div>
      </UiComponentWrapper>
    </UiComponentContainer>

    <!-- Section > Pricing -->
    <UiComponentWrapper class="pricing">
      <UiComponentContainer class="inner">
        <OrganismHero
          alignment="center"
          :level="2"
        >
          <template
            v-if="!props.page.isAnAddOn"
            #tag
          >
            {{ props.componentPricingCard.tag }}
          </template>
          <template #heading>
            {{
              props.page.pricingTitle || props.componentPricingCard.title
            }}
          </template>
          <template #paragraph>
            {{
              props.page.pricingDescription || props.componentPricingCard.text
            }}
          </template>
        </OrganismHero>
        <UiComponentWrapper
          v-if="props.page.isAnAddOn"
          class="action"
        >
          <AtomButton
            :action="requestDemoCallToAction.action"
            label="Contact section CTA"
            size="l"
            :to="requestDemoCallToAction.to"
          >
            {{ props.componentGlobal.contactSales }}
          </AtomButton>
        </UiComponentWrapper>
        <UiComponentWrapper
          v-else
          class="list"
        >
          <!-- Free plan -->
          <UiComponentWrapper
            class="item"
          >
            <UiComponentHeading
              as="paragraph"
              :level="3"
            >
              {{
                props.componentPricing.freeColumnTitle
              }}
            </UiComponentHeading>
            <UiComponentParagraph class="description">
              {{ currencySymbol === "$" ? currencySymbol : undefined
              }}{{ props.componentPricing.freeColumnPrice
              }}{{
                currencySymbol === "€" ? currencySymbol : undefined
              }}
            </UiComponentParagraph>
            <AtomButton
              :action="signupOrUpgradeCallToAction.action"
              label="Column Free CTA"
              size="l"
              :to="signupOrUpgradeCallToAction.to"
            >
              {{ signupOrUpgradeCallToAction.slot }}<UiComponentIcon type="arrow-right" />
            </AtomButton>
          </UiComponentWrapper>

          <!-- Pro plan -->
          <UiComponentWrapper
            class="item"
          >
            <UiComponentHeading
              as="paragraph"
              :level="3"
            >
              {{
                props.componentPricing.proColumnTitle
              }}
            </UiComponentHeading>
            <UiComponentParagraph class="description">
              <small>{{ props.componentWording.pricingSectionFrom }}</small>
              {{ currencySymbol === "$" ? currencySymbol : undefined
              }}{{ props.componentPricing.proColumnPrice[0].priceEurMonthly
              }}{{ currencySymbol === "€" ? currencySymbol : undefined
              }}<small>/{{ props.componentWording.pricingSectionPerMonth }}</small>
            </UiComponentParagraph>
            <AtomButton
              :action="signupOrUpgradeCallToAction.action"
              label="Column Pro CTA"
              size="l"
              :to="signupOrUpgradeCallToAction.to"
            >
              {{ signupOrUpgradeCallToAction.slot }}<UiComponentIcon type="arrow-right" />
            </AtomButton>
          </UiComponentWrapper>

          <!-- Business plan -->
          <UiComponentWrapper class="item">
            <UiComponentHeading
              as="paragraph"
              :level="3"
            >
              {{
                props.componentPricing.businessColumnTitle
              }}
            </UiComponentHeading>
            <UiComponentParagraph class="description">
              {{
                props.componentPricing.businessColumnPrice
              }}
            </UiComponentParagraph>
            <AtomButton
              :action="requestDemoCallToAction.action"
              label="Column Business CTA"
              size="l"
              :to="requestDemoCallToAction.to"
            >
              {{ props.componentGlobal.contactSales }}<UiComponentIcon type="arrow-right" />
            </AtomButton>
          </UiComponentWrapper>

          <!-- Enterprise plan -->
          <UiComponentWrapper class="item">
            <UiComponentHeading
              as="paragraph"
              :level="3"
            >
              {{
                props.componentPricing.enterpriseColumnTitle
              }}
            </UiComponentHeading>
            <UiComponentParagraph class="description">
              {{
                props.componentPricing.enterpriseColumnPrice
              }}
            </UiComponentParagraph>
            <AtomButton
              :action="requestDemoCallToAction.action"
              label="Column Enterprise CTA"
              size="l"
              :to="requestDemoCallToAction.to"
            >
              {{ props.componentGlobal.contactSales }}<UiComponentIcon type="arrow-right" />
            </AtomButton>
          </UiComponentWrapper>
        </UiComponentWrapper>
      </UiComponentContainer>
    </UiComponentWrapper>

    <!-- Section > Our Enterprise Plan -->
    <UiComponentWrapper class="our-enterprise-plan">
      <UiComponentContainer
        class="inner"
        @mouseenter="onMouseEnter"
        @mouseleave="onMouseLeave"
      >
        <lottie-player
          v-if="isLottieFrameworkLoaded"
          ref="lottie"
          class="background"
          src="/lottie/enterprise/lottie.json"
        />
        <UiComponentHeading
          as="paragraph"
          :level="3"
        >
          {{
            props.componentEnterprise.title
          }}
        </UiComponentHeading>
        <UiComponentParagraph>{{ props.componentEnterprise.description }}</UiComponentParagraph>
        <UiComponentWrapper class="actions">
          <AtomButton
            action="Demo clicked"
            label="Enterprise section CTA"
            size="l"
            to="#form-demo"
          >
            {{ props.componentGlobal.contactSales }}
          </AtomButton>
          <AtomButton
            action="Button clicked"
            label="Enterprise section CTA"
            size="l"
            :to="localePath('enterprise')"
            type="secondary"
          >
            {{ props.componentGlobal.learnMore }}
            <UiComponentIcon type="arrow-right" />
          </AtomButton>
        </UiComponentWrapper>
      </UiComponentContainer>
    </UiComponentWrapper>

    <!-- Section > Customers -->
    <UiComponentContainer class="our-customers">
      <OrganismCompaniesTrust
        :companies="companies"
        :call-to-action="
          props.page.isAnAddOn
            ? {
              ...requestDemoCallToAction,
              label: 'Customer section CTA',
              slot: props.componentGlobal.contactSales,
            }
            : {
              ...signupOrUpgradeCallToAction,
              label: 'Customer section CTA',
            }
        "
        :hover="props.componentCustomersReviewLogo.hoverText"
      >
        <template #heading>
          {{ props.componentCustomersReviewLogo.text }}
        </template>
      </OrganismCompaniesTrust>
    </UiComponentContainer>

    <!-- Section > FAQ -->
    <UiComponentWrapper
      v-if="faqItems.length > 0"
      class="frequently-asked-question"
    >
      <MoleculeImageFullWidthAndTitle
        :level="2"
        :image="props.page.useCasePicture"
      >
        <template #heading>
          {{ props.componentGlobal.faqTitle }}
        </template>
      </MoleculeImageFullWidthAndTitle>
      <UiComponentContainer
        class="inner"
        :start="1"
        :end="1"
      >
        <OrganismAccordion :items="faqItems" />
      </UiComponentContainer>
    </UiComponentWrapper>

    <!-- Section > Key Numbers -->
    <UiComponentContainer
      v-observe-visibility="onVisibilityOfKeyNumbersChange"
      class="key-numbers"
    >
      <UiComponentWrapper
        v-for="({ id, icon, content, text }, index) in props.componentKeyNumbers.map(
          ({ value, unit, ...props }) => ({
            ...props,
            content: `${value} ${unit}`.split('').map((character) => ({
              character,
              type: Number.isNaN(Number(character)) ? 'text' : 'number',
            })),
          }),
        )"
        :key="id"
        class="card"
      >
        <UiComponentImage
          v-if="icon"
          :alt="icon.alt"
          class="icon"
          contains
          :expected-size="[111.5, 126.47, 110.22, 104.32][index]"
          :height="icon.height"
          :source="icon.url"
          :width="icon.width"
        />
        <UiComponentWrapper class="title">
          <UiComponentWrapper
            v-for="({ character, type }, subindex) in content"
            :key="subindex"
            class="inner"
          >
            <UiComponentWrapper
              v-if="character === ' ' || type === 'text'"
              class="character"
            >
              {{ character }}
            </UiComponentWrapper>
            <UiComponentWrapper
              v-else
              class="numbers"
              :data-to="character"
            >
              <UiComponentWrapper
                class="slider"
                :data-index="character === '0' ? 10 : character"
              >
                <UiComponentWrapper class="number">
                  0
                </UiComponentWrapper>
                <UiComponentWrapper
                  v-for="n in 10"
                  :key="n"
                  class="number"
                >
                  {{
                    n % 10
                  }}
                </UiComponentWrapper>
              </UiComponentWrapper>
            </UiComponentWrapper>
          </UiComponentWrapper>
        </UiComponentWrapper>
        <UiComponentParagraph>{{ text }}</UiComponentParagraph>
      </UiComponentWrapper>
    </UiComponentContainer>

    <!-- Section > Reviews -->
    <UiComponentContainer class="reviews">
      <MoleculeReviewG2 :medals="props.componentCertification.medals">
        <template #tag>
          {{ props.componentCertification.tag }}
        </template>
        <template #heading>
          {{
            props.page.customersReviewsSectionTitle
              || props.componentCertification.title
          }}
        </template>
      </MoleculeReviewG2>
      <OrganismTestimonials
        :testimonials="testimonials"
        :to="localePath('customers')"
      >
        <template #testimonial>
          {{ props.componentGlobal.readTheCustomerStory }}
        </template>
        <template #call-to-action>
          {{ props.componentGlobal.readTheirStories }}
        </template>
      </OrganismTestimonials>
    </UiComponentContainer>

    <!-- Section > Get Started -->
    <UiComponentContainer
      class="get-started"
      :start="1"
      :end="1"
    >
      <OrganismCallToAction
        :features="livestormFeatures"
        :primary-button="{
          ...signupOrUpgradeCallToAction,
          label: 'Big section CTA',
        }"
        :secondary-button="{
          ...requestDemoCallToAction,
          label: 'Big section CTA',
        }"
        theme="new"
      >
        <template #heading>
          {{ props.componentGetStarted.title }}
        </template>
        <template #paragraph>
          {{ props.componentGetStarted.paragraph }}
        </template>
      </OrganismCallToAction>
    </UiComponentContainer>

    <!-- Section > Related Posts -->
    <OrganismHighlightedPosts
      v-if="props.page.relatedPosts.length >= 4 || props.componentRelatedPosts.items.length > 0"
      class="our-guides"
      :items="
        props.page.relatedPosts.length >= 4
          ? props.page.relatedPosts
          : props.componentRelatedPosts.items
      "
      :minutes-read="props.componentGlobal.minRead"
    >
      {{ props.page.relatedPostsTitle || props.componentRelatedPosts.title }}
    </OrganismHighlightedPosts>

    <!-- Section > Our Guides -->
    <OrganismHighlightedGuides
      v-if="props.allPillarPages.length > 0 || props.page.ourGuides.length > 0"
      class="our-guides"
      :items="
        props.page.ourGuides.length > 0
          ? props.page.ourGuides
          : props.allPillarPages
      "
    >
      {{ props.page.ourGuidesTitle || props.componentOurGuides.title }}
    </OrganismHighlightedGuides>

    <!-- Section > SEO Items -->
    <UiComponentContainer
      v-if="props.page.seoItems.length > 0"
      class="seo-items"
      :start="1"
      :end="1"
    >
      <UiComponentWrapper class="list">
        <UiComponentWrapper
          v-for="{ id, title, titleLevel, text, image, video } in props.page.seoItems"
          :key="`seo-item-${id}`"
          class="item"
        >
          <UiComponentHeading :level="titleLevel || 2">
            {{ title }}
          </UiComponentHeading>
          <div
            class="text"
            v-html="$md.render(text)"
          />
          <UiComponentWrapper
            v-if="image"
            class="image"
          >
            <UiComponentWrapper class="inner">
              <UiComponentImage
                :key="`seo-item-${id}-image-${seoItemImageSize}`"
                :alt="image.alt"
                :expected-size="seoItemImageSize"
                :height="image.height"
                :orientation="image.width >= image.height ? 'landscape' : 'portrait'"
                :source="image.url"
                :width="image.width"
              />
            </UiComponentWrapper>
          </UiComponentWrapper>
          <UiComponentWrapper
            v-if="video && video.provider === 'youtube'"
            class="video"
          >
            <UiComponentWrapper class="inner">
              <iframe
                :src="`https://www.youtube.com/embed/${video.providerUid}`"
                title="YouTube video player"
                frameborder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerpolicy="strict-origin-when-cross-origin"
                allowfullscreen
              />
            </UiComponentWrapper>
          </UiComponentWrapper>
        </UiComponentWrapper>
      </UiComponentWrapper>
    </UiComponentContainer>
  </UiComponentWrapper>
</template>

<script setup>
import Glide from '@glidejs/glide'
import inEU from '@segment/in-eu'
import { gsap } from 'gsap'

// Composables
const { page } = useAnalyticsJS()
const { updateListItems } = useBreadcrumb()
const { isDesktop, isMobileOrTablet } = useDevice()
const { locale } = useI18n()
const { isLogged } = useIsLoggedIn()
const localePath = useLocalePath()
const { $md } = useMarkdown()
const { $lottiePlayer } = useNuxtApp()
const getRouteBaseName = useRouteBaseName()
const { public: publicRuntimeConfig } = useRuntimeConfig()
const { width: screenWidth } = useScreenSize()

// Directives
const { vObserveVisibility } = useVObserveVisibility()

// Props
const props = defineProps({
  allPillarPages: {
    required: true,
    type: Array,
  },
  componentCertification: {
    required: true,
    type: Object,
  },
  componentCustomersReviewLogo: {
    required: true,
    type: Object,
  },
  componentEnterprise: {
    required: true,
    type: Object,
  },
  componentGetStarted: {
    required: true,
    type: Object,
  },
  componentGlobal: {
    required: true,
    type: Object,
  },
  componentHeroReview: {
    required: true,
    type: Object,
  },
  componentKeyNumbers: {
    required: true,
    type: Array,
  },
  componentOurGuides: {
    required: true,
    type: Object,
  },
  componentPricing: {
    required: true,
    type: Object,
  },
  componentPricingCard: {
    required: true,
    type: Object,
  },
  componentRelatedPosts: {
    required: true,
    type: Object,
  },
  componentWording: {
    required: true,
    type: Object,
  },
  home: {
    required: true,
    type: Object,
  },
  page: {
    required: true,
    type: Object,
  },
})

// Refs
const benefitsBackgroundHeight = ref(0)
const benefitsBackgroundWidth = ref(0)
const benefitsBackgroundX = ref(0)
const benefitsBackgroundY = ref(0)
const benefitsObserver = ref(null)
const currencies = ref(['€ EUR', '$ USD'])
const isLottieAnimationLoaded = ref(false)
const isLottieFrameworkLoaded = ref(false)
const relatedUseCasesActiveIndex = ref(0)
const relatedUseCasesGlide = ref(null)
const selectedCurrencyIndex = ref(0)

// Variables
let keyNumbersTimeline = null

// Computed Properties
const allFeatures = computed(() => {
  const type = (
    props.page.contentType?.name || 'default'
  ).toLowerCase()
  return [
    {
      id: '1',
      title: props.page.feature1Title,
      features: props.page.feature1Features
        .map(feature => ({
          id: feature.id,
          title: feature.title,
          text:
            feature[`${type}Description`]
            || feature.defaultDescription,
          enterpriseOnly: feature.enterprisePlanOnly,
        }))
        .slice(0, 3),
      cta:
        props.page.feature1CTALink
        && props.page.feature1CTAWording
          ? {
              slot: props.page.feature1CTAWording,
              url: props.page.feature1CTALink,
            }
          : null,
    },
    {
      id: '2',
      title: props.page.feature2Title,
      features: props.page.feature2Features
        .map(feature => ({
          id: feature.id,
          title: feature.title,
          text:
            feature[`${type}Description`]
            || feature.defaultDescription,
          enterpriseOnly: feature.enterprisePlanOnly,
        }))
        .slice(0, 3),
      cta:
        props.page.feature2CTALink
        && props.page.feature2CTAWording
          ? {
              slot: props.page.feature2CTAWording,
              url: props.page.feature2CTALink,
            }
          : null,
    },
    {
      id: '3',
      title: props.page.feature3Title,
      features: props.page.feature3Features
        .map(feature => ({
          id: feature.id,
          title: feature.title,
          text:
            feature[`${type}Description`]
            || feature.defaultDescription,
          enterpriseOnly: feature.enterprisePlanOnly,
        }))
        .slice(0, 3),
      cta:
        props.page.feature3CTALink
        && props.page.feature3CTAWording
          ? {
              slot: props.page.feature3CTAWording,
              url: props.page.feature3CTALink,
            }
          : null,
    },
  ]
})

const companies = computed(() => {
  return props.componentCustomersReviewLogo.customers
    .filter(({ logoColor: logo }) => !!logo)
    .map(customer => ({
      alt: `${customer.name} logo`,
      height: 48,
      params: {
        h: 48,
        w: 'auto',
      },
      url: customer.logoColor.url,
      width: Math.round(
        48 * (customer.logoColor.width / customer.logoColor.height),
      ),
    }))
    .slice(0, 10)
})

const currencySymbol = computed(() => {
  return currencies.value[selectedCurrencyIndex.value].charAt(0)
})

const faqItems = computed(() => {
  return props.page?.faq.map(item => ({
    title: item.title,
    content: $md.render(item.text),
  }))
})

const heroImageWidth = computed(() => {
  switch (true) {
    case screenWidth.value < 768:
      return screenWidth.value - 32
    case screenWidth.value >= 768 && screenWidth.value < 1024:
      return screenWidth.value - 48
      break
    case screenWidth.value >= 1024 && screenWidth.value < 1328:
      return (screenWidth.value - 48) * (620 / 1328)
    default:
      return 620
  }
})

const heroLogos = computed(() => {
  return props.page.heroHighlightedCustomers
    .filter(({ logo }) => !!logo)
    .map(({ id, logo, name }) => ({
      id,
      logo,
      name,
    }))
})

const highlightedFeatureImageSmallWidth = computed(() => {
  switch (true) {
    case screenWidth.value < 768:
      return screenWidth.value - 104
    case screenWidth.value >= 768 && screenWidth.value < 1328:
      return (screenWidth.value - 48 - 40) / 2 - 72
    default:
      return 406
  }
})

const highlightedFeatureImageLargeWidth = computed(() => {
  switch (true) {
    case screenWidth.value < 768:
      return screenWidth.value - 104
    case screenWidth.value >= 768 && screenWidth.value < 1328:
      return (screenWidth.value - 48 - 40) / 2 - 72
    default:
      return 626
  }
})

const highlightedFeatures = computed(() => {
  const type = props.page.contentType.name.toLowerCase()
  return props.page.highlightedFeatures.map((feature) => {
    return {
      id: feature.id,
      title: feature.title,
      description:
        feature[`${type}Description`] || feature.defaultDescription,
      image: feature[`${type}Image`] || feature.defaultImage,
      theme: feature.themeColor?.color || 'livestorm-blue',
    }
  })
})

const livestormFeatures = computed(() => {
  return [
    props.componentGlobal.noCreditCard,
    props.componentGlobal.noInstall,
  ]
})

const relatedUseCases = computed(() => {
  return props.page?.useCases.map(
    ({ useCase: feature, description }) => ({
      id: feature.id,
      title: feature.shortName,
      description: description || feature.description,
      hasRichDescription: !!description,
      icon: feature.icon,
      image: feature.image,
      slug: feature.slug,
      theme: feature.themeColor.color,
      to: localePath({
        name: 'use-cases-slug',
        params: { slug: feature.slug },
      }),
    }),
  )
})

const requestDemoCallToAction = computed(() => {
  return {
    to: '#form-demo',
    action: 'Demo clicked',
    slot: props.componentGlobal?.ctaGetDemo,
  }
})

const seoItemImageSize = computed(() => {
  switch (true) {
    case screenWidth.value < 768:
      return screenWidth.value - 32
    case screenWidth.value >= 768 && screenWidth.value < 1328:
      return Math.min(Math.max(screenWidth.value - 48, 1), 840)
    default:
      return 840
  }
})

const signupOrUpgradeCallToAction = computed(() => {
  return isLogged.value
    ? {
        action: 'Upgrade clicked',
        to: `https://${publicRuntimeConfig.appBaseUrl}/#/settings?page=settings&tab=billing`,
        slot: props.componentGlobal.ctaUpgradeNow,
      }
    : {
        action: 'Signup clicked',
        to: `https://${publicRuntimeConfig.appBaseUrl}/#/signup`,
        slot: props.componentGlobal.ctaGetStartedForFree,
      }
})

const testimonials = computed(() => {
  return props.page.customerSection.map(({ customer }) => ({
    customer: {
      avatar: {
        alt: `${customer.authorUserStory}' Avatar`,
        height: customer.authorPicture.height,
        url: customer.authorPicture.url,
        width: customer.authorPicture.width,
      },
      name: customer.authorUserStory,
      position: customer.authorPosition,
    },
    logo: {
      alt: `${customer.name}' logo`,
      height: customer.logoColor.height,
      url: customer.logoColor.url,
      width: customer.logoColor.width,
    },
    quote: customer.quote,
    slug:
      customer?.contentUserStory.length > 0
        ? localePath({
            name: 'customers-slug',
            params: { slug: customer?.slug },
          })
        : undefined,
  }))
})

// Watchers
watch(() => screenWidth.value, (value) => {
  if (value) {
    createKeyNumbersTimeline()
  }
})

// Methods
function createKeyNumbersTimeline() {
  keyNumbersTimeline?.kill()
  keyNumbersTimeline = null

  const $cards = [...document.querySelectorAll('.page .key-numbers .card')]
  const keyNumbersSliderSizes = [
    [23, 29],
    [13, 17],
    [19, 24],
    [19, 24],
    [20, 25],
    [20, 25],
    [20, 29],
    [17, 21],
    [21, 26],
    [21, 26],
  ]
  const subindex = screenWidth.value < 768 ? 0 : 1

  keyNumbersTimeline = gsap.timeline()
  $cards.forEach(($card, i) => {
    [...$card.querySelectorAll('.slider')].forEach(($el, j) => {
      const $parent = $el.parentNode
      keyNumbersTimeline.to(
        $parent,
        {
          width:
            keyNumbersSliderSizes[Number($parent.dataset.to)][subindex],
          duration: 1,
          ease: 'expo.inOut',
        },
        i * 0.05 + j * 0.1,
      )
      keyNumbersTimeline.to(
        $el,
        {
          yPercent: -100 * Number.parseInt($el.dataset.index),
          duration: 1,
          ease: 'expo.inOut',
        },
        i * 0.05 + j * 0.1,
      )
    })
  })
  keyNumbersTimeline.pause()
}

function onObserveBenefitsSection() {
  const dpr = 3
  const { clientWidth } = document.documentElement
  const { offsetHeight, offsetWidth }
    = document.querySelector('.benefits .inner')
  benefitsBackgroundHeight.value = offsetHeight * dpr
  benefitsBackgroundWidth.value = offsetWidth * dpr
  switch (true) {
    case clientWidth < 768:
      benefitsBackgroundX.value = 1392 * dpr
      benefitsBackgroundY.value = 1816 * dpr
      break
    case clientWidth >= 768 && clientWidth < 1328:
      benefitsBackgroundX.value = 1392 * dpr
      benefitsBackgroundY.value = 2000 * dpr
      break
    default:
      benefitsBackgroundX.value = 933 * dpr
      benefitsBackgroundY.value = 2019 * dpr
  }
}

function onMouseEnter() {
  if (isLottieAnimationLoaded.value && isDesktop) {
    const player = document.querySelector('.page lottie-player')
    player.setDirection(1)
    player.play()
  }
}

function onMouseLeave() {
  if (isLottieAnimationLoaded.value && isDesktop) {
    const player = document.querySelector('.page lottie-player')
    player.setDirection(-1)
    player.play()
  }
}

function onReady() {
  isLottieAnimationLoaded.value = true

  if (isMobileOrTablet) {
    const player = document.querySelector('.page lottie-player')
    player.seek('100%')
    player.play()
  }
}

function onVisibilityOfKeyNumbersChange(isVisible) {
  if (isVisible) {
    keyNumbersTimeline?.play()
  }
  else {
    keyNumbersTimeline?.reverse()
  }
}

// Lifecycle hooks
onBeforeUnmount(() => {
  benefitsObserver.value.unobserve(document.querySelector('.page .benefits'))

  keyNumbersTimeline?.kill()
  keyNumbersTimeline = null

  relatedUseCasesGlide.value?.destroy()
})

onMounted(() => {
  page(
    props.page?.trackName || props.page?.trackNameFallback,
  )
  updateListItems([
    {
      item: localePath('index'),
      name: props.home.trackingBreadcrumbTitle,
    },
    {
      item: localePath({ name: getRouteBaseName() }),
      name: props.page.trackingBreadcrumbTitle,
    },
  ])

  benefitsObserver.value = new ResizeObserver(onObserveBenefitsSection)
  benefitsObserver.value.observe(document.querySelector('.page .benefits'))

  if (document.querySelector('.page .use-cases')) {
    relatedUseCasesGlide.value = new Glide('.page .use-cases .desktop .glide', {
      type: 'slider',
      gap: 0,
      perView: 1,
    })
    relatedUseCasesGlide.value.on('run.after', () => {
      relatedUseCasesActiveIndex.value = relatedUseCasesGlide.value.index
    })
    relatedUseCasesGlide.value.mount()
  }

  if (!inEU()) {
    selectedCurrencyIndex.value = 1
  }

  $lottiePlayer.init(() => {
    isLottieFrameworkLoaded.value = true
    nextTick(() => {
      const player = document.querySelector('lottie-player')
      player.addEventListener('ready', onReady)
    })
  })
})
</script>

<style lang="postcss" scoped>
@import "../../../assets/css/glide.css";

.page {
  @apply relative flex-col items-center;

  .gradient {
    @apply absolute left-0 right-0 z-0;
    background: linear-gradient(
      180deg,
      #f0f4ff 71.35%,
      rgba(240, 244, 255, 0) 100%
    );
    height: 1580px;
    top: -56px;

    @screen md {
      @apply -top-[72px];
    }

    @screen xl {
      height: 1056px;
    }
  }

  .jumbotron {
    @apply flex-col pt-12 pb-20 z-10 overflow-hidden;

    @screen md {
      @apply pb-34;
    }

    @screen lg {
      @apply flex-row flex-wrap justify-between items-center pt-16 overflow-visible;
    }

    .content {
      @apply flex-col;

      @screen lg {
        @apply w-span5;
      }

      :deep(.hero) {
        .tag {
          @apply bg-livestorm-blue-200;
        }

        .h1 {
          @screen mdminus {
            @apply text-3xl leading-10;
          }

          @screen mdplus {
            @apply text-5xl leading-16;
          }
        }
      }

      :deep(.review) {
        @apply flex-col items-center mt-8;

        @screen xl {
          @apply items-start;
        }

        .rating {
          @apply relative justify-center w-auto cursor-pointer;

          @screen xl {
            @apply justify-start;
          }

          &::before,
          &::after {
            @apply opacity-0 invisible transition-all duration-300 ease-linear;
          }

          &::before {
            @apply absolute flex justify-center items-center bg-grey-blue-100 text-center text-origin text-winter-green-900 leading-7 shadow-light4 z-20;
            content: attr(data-hover);
            border-radius: 12px;
            padding: 14px 24px;
            transform: translateY(calc(-100% - 1rem));
          }

          &::after {
            @apply absolute flex h-5 w-5 bg-grey-blue-100 transform origin-center rotate-45 z-30;
            content: "";
            border-radius: 3px;
            left: calc(50% - 10px);
            top: -30px;
          }

          &:hover {
            &::before,
            &::after {
              @screen md {
                @apply opacity-100 visible;
              }
            }
          }

          .stars {
            @apply w-auto;
            margin-right: 10px;
          }

          .copy {
            @apply flex w-auto whitespace-pre;

            p {
              @apply text-center text-origin text-grey-blue-800 leading-7;

              @screen lg {
                @apply text-left;
              }

              strong {
                @apply font-medium text-grey-blue-800 !important;
              }
            }
          }
        }

        .customers {
          @apply justify-center flex-wrap mt-4;

          @screen md {
            @apply justify-between;
            max-width: 624px;
          }

          @screen xl {
            @apply max-w-full;
          }

          .logo {
            @apply flex-shrink-0 justify-center w-1/2 mb-8;

            @screen md {
              @apply w-auto mb-0;
            }

            picture {
              @apply rounded-none;

              img {
                filter: invert(45%) sepia(13%) saturate(601%) hue-rotate(163deg)
                  brightness(87%) contrast(84%);
              }
            }
          }
        }
      }
    }

    .animation {
      @apply relative mt-12;

      @screen lg {
        @apply w-span6 mt-0;
      }

      &::before {
        @apply absolute flex transform -translate-x-1/2 -translate-y-1/2 z-0;
        background: radial-gradient(
          50% 50% at 50% 50%,
          #dce6fe 0%,
          rgba(220, 230, 254, 0) 100%
        );
        content: "";
        height: 415px;
        width: 415px;
        top: 50%;
        left: 50%;

        @screen md {
          height: 751px;
          width: 751px;
        }

        @screen xl {
          height: 755px;
          width: 755px;
        }
      }

      picture {
        @apply rounded-xs;

        @screen md {
          @apply rounded;
        }

        &.has-shadow {
          @apply bg-grey-blue-100 shadow-light2;
        }
      }
    }
  }

  .benefits {
    @apply z-10;

    .inner {
      @apply relative flex-col p-8 bg-livestorm-blue-200 shadow-light3 rounded-4xl;

      @screen md {
        @apply p-12;
      }

      @screen lg {
        @apply p-16;
      }

      picture {
        @apply absolute inset-0 rounded-4xl z-0;
      }

      :deep(.hero) {
        @apply z-10;

        p {
          @apply text-grey-blue-700;
        }
      }

      .list {
        @apply flex-col mt-16 z-10;

        @screen xl {
          @apply flex-row flex-wrap justify-between;
        }

        .item {
          @apply flex-col mt-8;

          @screen xl {
            @apply mt-0;
            max-width: 357px;
          }

          &:first-child {
            @apply mt-0;
          }

          .h4 {
            @screen xl {
              max-width: 290px;
            }
          }

          .h4 + p {
            @apply mt-4 text-grey-blue-700;
          }
        }
      }
    }
  }

  .features {
    @apply flex-col mt-20;

    @screen md {
      @apply mt-34;
    }

    .highlighted {
      @apply flex-col mt-16;

      @screen md {
        @apply flex-row flex-wrap;
      }

      .item {
        @apply relative flex-col mt-10 p-8 border-4 border-white/50 rounded-4xl overflow-hidden;

        @screen md {
          @apply h-full min-h-[458px] max-h-[458px] mx-5 pb-0;
          width: calc((100% - 40px) / 2);
        }

        @screen xl {
          @apply min-h-[510px] max-h-[510px] p-12 pb-0;
        }

        &.blizzard {
          @apply bg-blizzard-200;
        }
        &.grey-blue {
          @apply bg-grey-blue-200;
        }
        &.live-red {
          @apply bg-live-red-200;
        }
        &.livestorm-blue {
          @apply bg-livestorm-blue-200;
        }
        &.winter-green {
          @apply bg-winter-green-200;
        }
        &.sandstorm-yellow {
          @apply bg-sandstorm-yellow-200;
        }
        &.sirocco {
          @apply bg-sirocco-200;
        }
        &.sunburst-red {
          @apply bg-sunburst-red-200;
        }

        .gradient {
          @apply absolute -left-[327px] -top-[328px] h-[655px] w-[655px] max-w-[unset] rounded-full z-0;
          background: radial-gradient(
            50% 50% at 50% 50%,
            rgba(255, 255, 255, 0.8) 0%,
            rgba(255, 255, 255, 0) 90%,
            rgba(255, 255, 255, 0) 90%
          );

          @screen md {
            @apply -left-[413px] -top-[413px] h-[827px] w-[827px];
          }
        }

        &:nth-child(1) {
          @apply mt-0;
        }

        &:nth-child(2) {
          @screen md {
            @apply mt-0;
          }
        }

        &:nth-child(4n + 1) {
          @screen md {
            @apply ml-0;
          }

          @screen xl {
            @apply w-span7;
          }
        }

        &:nth-child(4n + 2) {
          @screen md {
            @apply mr-0;
          }

          @screen xl {
            @apply w-span5;
          }
        }

        &:nth-child(4n + 3) {
          @screen md {
            @apply ml-0;
          }

          @screen xl {
            @apply w-span5;
          }
        }

        &:nth-child(4n + 4) {
          @screen md {
            @apply mr-0;
          }

          @screen xl {
            @apply w-span7;
          }
        }

        h3 {
          @apply text-xl leading-7;

          @screen md {
            @apply text-2xl leading-8 !important;
          }
        }

        h3,
        p {
          @apply z-10;

          @screen lg {
            @apply max-w-[462px];
          }
        }

        h3 + p {
          @apply mt-2 text-grey-blue-600;
        }

        picture {
          @apply mt-6 rounded-none z-10;

          &.has-shadow {
            @apply rounded-xs shadow-light2;
          }
        }
      }
    }

    .list {
      @apply flex-col mt-20;

      @screen md {
        @apply mt-32;
      }

      .item {
        @apply flex-col pt-8;

        @screen lg {
          @apply flex-row flex-wrap justify-between pt-16;
        }

        &:first-child {
          @apply pt-0;
        }

        &.has-border {
          @apply pb-8 border-b border-grey-blue-200;

          @screen lg {
            @apply pb-16;
          }
        }

        .head {
          @apply flex-col;

          @screen lg {
            @apply w-span3;
          }

          .h3 {
            @apply text-2xl leading-8 !important;
          }

          .button {
            @apply hidden self-start h-[50px] mt-6 px-0 text-grey-blue-600 font-bold;

            @screen lg {
              @apply flex;
            }

            :deep(i) {
              @apply text-grey-blue-600;
            }
          }
        }

        .body {
          @apply flex-col mt-8;

          @screen lg {
            @apply flex-row flex-wrap w-span9 mt-0;
          }

          .feature {
            @apply flex-col mt-6;

            @screen lg {
              @apply mx-5 mt-0;
              width: calc((100% - 80px) / 3);
            }

            &:first-child {
              @apply mt-0;
            }

            &:nth-child(3n + 1) {
              @screen lg {
                @apply ml-0;
              }
            }

            &:nth-child(3n + 3) {
              @screen lg {
                @apply mr-0;
              }
            }

            .enterprise-only {
              @apply flex self-start w-auto mt-2 px-2 py-1 bg-grey-blue-200 font-medium text-grey-blue-600 text-xs leading-4;
              border-radius: 8px;

              img {
                @apply mr-1;
              }
            }

            .h5 + p,
            .enterprise-only + p {
              @apply mt-4 text-grey-blue-600;
            }
          }
        }

        .footer {
          @apply mt-6;

          @screen lg {
            @apply hidden;
          }

          .button {
            @apply h-[50px] px-0 text-grey-blue-600 font-bold;

            :deep(i) {
              @apply text-grey-blue-600;
            }
          }
        }
      }
    }
  }

  .use-cases {
    @apply flex-col items-center mt-26;

    @screen md {
      @apply mt-46;
    }

    :deep(.hero) {
      h2 {
        @apply font-normal !important;

        @screen md {
          @apply text-5xl leading-16;
        }

        @screen xl {
          max-width: 1000px;
        }

        b,
        strong {
          @apply font-normal !important;
        }
      }
    }

    .mobile {
      @apply flex-col mt-14;

      @screen md {
        @apply flex-row flex-wrap mt-20;
      }

      @screen xl {
        @apply hidden;
      }

      .use-case {
        @apply flex-col mt-14;

        &:first-child {
          @apply mt-0;
        }

        &.blizzard {
          .background {
            @apply bg-blizzard-100 border-blizzard-300;

            i {
              @apply text-blizzard-500;
            }
          }

          :deep(.description) {
            ul {
              li {
                &::before {
                  @apply text-blizzard-400 !important;
                }
              }
            }
          }
        }

        &.grey-blue {
          .background {
            @apply bg-grey-blue-100 border-grey-blue-300;

            i {
              @apply text-grey-blue-500;
            }
          }

          :deep(.description) {
            ul {
              li {
                &::before {
                  @apply text-grey-blue-600 !important;
                }
              }
            }
          }
        }

        &.livestorm-blue {
          .background {
            @apply bg-livestorm-blue-100 border-livestorm-blue-300;

            i {
              @apply text-livestorm-blue-500;
            }
          }

          :deep(.description) {
            ul {
              li {
                &::before {
                  @apply text-livestorm-blue-400 !important;
                }
              }
            }
          }
        }

        &.live-red {
          .background {
            @apply bg-live-red-100 border-live-red-300;

            i {
              @apply text-live-red-500;
            }
          }

          :deep(.description) {
            ul {
              li {
                &::before {
                  @apply text-live-red-400 !important;
                }
              }
            }
          }
        }

        &.sirocco {
          .background {
            @apply bg-sirocco-100 border-sirocco-300;

            i {
              @apply text-sirocco-500;
            }
          }

          :deep(.description) {
            ul {
              li {
                &::before {
                  @apply text-sirocco-400 !important;
                }
              }
            }
          }
        }

        &.sunburst-red {
          .background {
            @apply bg-sunburst-red-100 border-sunburst-red-300;

            i {
              @apply text-sunburst-red-500;
            }
          }

          :deep(.description) {
            ul {
              li {
                &::before {
                  @apply text-sunburst-red-400 !important;
                }
              }
            }
          }
        }

        @screen md {
          @apply mt-16;
          width: calc(calc(100% - 2.5rem) / 2);

          &:nth-child(1),
          &:nth-child(2) {
            @apply mt-0;
          }

          &:nth-child(odd) {
            @apply ml-0 mr-5;
          }

          &:nth-child(even) {
            @apply ml-5 mr-0;
          }
        }

        .background {
          @apply items-center justify-center h-14 w-14 border-2;
          border-radius: 16px;

          i {
            @apply text-2xl leading-none;
          }
        }

        .h3 {
          @apply mt-4 font-medium text-origin leading-7;

          @screen mdminus {
            @apply text-xl;
          }

          @screen mdplus {
            @apply text-xl;
          }
        }

        .h3 + p {
          @apply mt-2 text-grey-blue-600;
        }

        .h3 + .description {
          @apply mt-2;
        }

        :deep(.description) {
          p,
          li {
            @apply text-origin text-grey-blue-600 leading-7;
          }

          ul {
            @apply mt-2;

            li {
              @apply flex flex-row items-baseline mt-2;

              &:first-child {
                @apply mt-0;
              }

              &::before {
                @apply w-4 h-4 flex justify-center items-center flex-shrink-0 text-livestorm-blue-700 mr-2 text-sm leading-4 antialiased font-icon content-['\e907'];
              }
            }
          }
        }

        .button {
          @apply self-start mt-4 px-0 font-semibold;
        }
      }
    }

    .desktop {
      @apply relative hidden;

      @screen xl {
        @apply flex flex-col mt-12;
      }

      .list {
        @apply flex-wrap justify-center;

        .item {
          @apply items-center w-auto h-11 mb-4 px-3 bg-transparent leading-11 cursor-pointer;
          border-radius: 1rem;

          &.active {
            @apply bg-grey-blue-100 cursor-default;

            p {
              @apply text-grey-blue-900;
            }
          }

          p {
            @apply font-medium text-grey-blue-600;
          }
        }
      }

      .slider {
        @apply mt-6;

        .inner {
          .use-case {
            @apply flex flex-row flex-wrap items-center justify-between p-12 rounded-4xl;

            &.blizzard {
              @apply bg-blizzard-100;

              .background {
                @apply border-blizzard-300;

                i {
                  @apply text-blizzard-500;
                }
              }

              :deep(.description) {
                ul {
                  li {
                    &::before {
                      @apply text-blizzard-400 !important;
                    }
                  }
                }
              }
            }

            &.grey-blue {
              @apply bg-grey-blue-100;

              .background {
                @apply border-grey-blue-300;

                i {
                  @apply text-grey-blue-500;
                }
              }

              :deep(.description) {
                ul {
                  li {
                    &::before {
                      @apply text-grey-blue-600 !important;
                    }
                  }
                }
              }
            }

            &.livestorm-blue {
              @apply bg-livestorm-blue-100;

              .background {
                @apply border-livestorm-blue-300;

                i {
                  @apply text-livestorm-blue-500;
                }
              }

              :deep(.description) {
                ul {
                  li {
                    &::before {
                      @apply text-livestorm-blue-400 !important;
                    }
                  }
                }
              }
            }

            &.live-red {
              @apply bg-live-red-100;

              .background {
                @apply border-live-red-300;

                i {
                  @apply text-live-red-500;
                }
              }

              :deep(.description) {
                ul {
                  li {
                    &::before {
                      @apply text-live-red-400 !important;
                    }
                  }
                }
              }
            }

            &.sirocco {
              @apply bg-sirocco-100;

              .background {
                @apply border-sirocco-300;

                i {
                  @apply text-sirocco-500;
                }
              }

              :deep(.description) {
                ul {
                  li {
                    &::before {
                      @apply text-sirocco-400 !important;
                    }
                  }
                }
              }
            }

            &.sunburst-red {
              @apply bg-sunburst-red-100;

              .background {
                @apply border-sunburst-red-300;

                i {
                  @apply text-sunburst-red-500;
                }
              }

              :deep(.description) {
                ul {
                  li {
                    &::before {
                      @apply text-sunburst-red-400 !important;
                    }
                  }
                }
              }
            }

            .content {
              @apply flex-col;

              @screen xl {
                max-width: 462px;
              }

              .background {
                @apply items-center justify-center h-14 w-14 border-2;
                border-radius: 16px;

                i {
                  @apply text-2xl leading-none;
                }
              }

              .h3 {
                @apply mt-4 font-medium text-origin leading-7;

                @screen mdminus {
                  @apply text-xl;
                }

                @screen mdplus {
                  @apply text-xl;
                }
              }

              .h3 + p {
                @apply mt-2 text-grey-blue-600;
              }

              .h3 + .description {
                @apply mt-2;
              }

              :deep(.description) {
                p,
                li {
                  @apply text-origin text-grey-blue-600 leading-7;
                }

                ul {
                  @apply mt-2;

                  li {
                    @apply flex flex-row items-baseline mt-2;

                    &:first-child {
                      @apply mt-0;
                    }

                    &::before {
                      @apply w-4 h-4 flex justify-center items-center flex-shrink-0 text-livestorm-blue-700 mr-2 text-sm leading-4 antialiased font-icon content-['\e907'];
                    }
                  }
                }
              }

              .button {
                @apply self-start mt-4 px-0 font-semibold;
              }
            }

            .picture {
              @apply rounded;
              max-width: 682px;
            }
          }
        }
      }

      .next,
      .prev {
        @apply absolute z-10 text-2xl cursor-pointer transform translate-y-1/2 transition-opacity duration-500 ease-in-out;
        bottom: 291px;

        &.disabled {
          @apply opacity-10 cursor-default;
        }
      }

      .next {
        right: -85px;
      }

      .prev {
        left: -85px;
      }
    }
  }

  .componentPricing {
    @apply justify-center mt-20 py-26 bg-blizzard-100;

    @screen md {
      @apply mt-34;
    }

    .hero {
      :deep(.tag) {
        @apply bg-white;
      }
    }

    .inner {
      @apply flex-col items-center;

      .list {
        @apply flex-col mt-12;

        @screen md {
          @apply flex-row flex-wrap justify-between;
        }

        .item {
          @apply flex-col mt-6 p-8 bg-white border-2 border-blizzard-200;
          border-radius: 26px;

          @screen md {
            @apply mt-10;
            width: calc(calc(100% - 2.5rem) / 2);
          }

          @screen xl {
            @apply mt-0;
            width: calc(calc(100% - 7.5rem) / 4);
          }

          &:nth-child(1) {
            @apply mt-0;
          }

          &:nth-child(2) {
            @screen md {
              @apply mt-0;
            }
          }

          &:nth-child(4) {
            .button {
              @apply bg-winter-green-900;
            }
          }

          .description {
            @apply inline-flex items-baseline mb-auto font-medium text-xl leading-7 whitespace-normal !important;
            margin-top: 3px;

            small {
              @apply inline-flex font-normal text-sm leading-6;

              &:first-child {
                @apply mr-1;
              }
            }
          }

          .button {
            margin-top: 17px;

            i {
              @apply text-white;
            }
          }
        }
      }
    }
  }

  .pricing {
    @apply justify-center mt-20 py-26 bg-blizzard-100;

    @screen md {
      @apply mt-34;
    }

    .hero {
      :deep(.tag) {
        @apply bg-white;
      }
    }

    .inner {
      @apply flex-col items-center;

      .list {
        @apply flex-col mt-12 gap-6;

        @screen md {
          @apply flex-row flex-wrap justify-center gap-10;
        }

        .item {
          @apply flex-col p-8 bg-white border-2 border-blizzard-200 rounded-[26px];

          @screen md {
            width: calc(calc(100% - 2.5rem) / 2);
          }

          @screen xl {
            width: calc(calc(100% - 7.5rem) / 4);
          }

          &.enterprise {
            .button {
              @apply bg-winter-green-900;
            }
          }

          .description {
            @apply inline-flex items-baseline mb-auto font-medium text-xl leading-7 whitespace-normal !important;
            margin-top: 3px;

            small {
              @apply inline-flex font-normal text-sm leading-6;

              &:first-child {
                @apply mr-1;
              }
            }
          }

          .button {
            margin-top: 17px;

            i {
              @apply text-white;
            }
          }
        }
      }

      .action {
        @apply justify-center mt-6;
      }
    }
  }

  .our-enterprise-plan {
    @apply justify-center pt-14 px-4;

    @screen md {
      @apply pt-26 px-6;
    }

    @screen xl {
      @apply px-0;
    }

    .inner {
      @apply relative flex-col mt-14 p-6 bg-winter-green-900 rounded-4xl overflow-hidden;
      padding-bottom: 168px;

      @screen md {
        @apply p-10;
      }

      @screen xl {
        @apply p-20;
        max-width: 1060px;
      }

      .background {
        @apply absolute z-0;
        top: 209.5px;
        right: -111px;
        height: 526px;
        width: 526px;

        @screen md {
          top: -24.5px;
          right: -131px;
          height: 496px;
          width: 496px;
        }

        @screen xl {
          top: -202px;
          right: -132px;
          height: 815px;
          width: 815px;
        }
      }

      .h3 {
        @apply text-white z-10;
        max-width: 537px;
      }

      .h3 + p {
        @apply mt-4 text-white leading-7 z-10;
        max-width: 537px;
      }

      .actions {
        @apply flex-col mt-6 z-10;

        @screen md {
          @apply flex-row;
        }

        .button + .button {
          @apply mt-4;

          @screen md {
            @apply mt-0 ml-6;
          }
        }
      }
    }
  }

  .our-customers {
    @apply flex-col mt-20;

    @screen md {
      @apply mt-34;
    }

    :deep(.companies-trust) {
      .headline {
        @apply order-first;
      }

      .companies {
        @apply mt-0;
      }
    }
  }

  .frequently-asked-question {
    @apply flex-col items-center mt-20;

    @screen md {
      @apply mt-46;
    }

    @screen xl {
      @apply mt-34;
    }

    .inner {
      @apply mt-10;

      @screen md {
        @apply mt-16;
      }

      @screen xl {
        @apply mt-24;
      }
    }
  }

  .key-numbers {
    @apply flex-col mt-20;

    @screen md {
      @apply flex-row flex-wrap mt-46;
    }

    .card {
      @apply relative flex-col bg-live-red-100 rounded-[24px] mt-10 p-8 overflow-hidden;
      height: 164px;

      @screen md {
        @apply mx-5 mt-10;
        height: 186px;
        width: calc((100% - 40px) / 2);
      }

      @screen lg {
        @apply mt-0;
        height: auto;
        width: calc((100% - 120px) / 4);
      }

      &::before {
        @apply absolute flex z-0;
        content: "";
        height: 350px;
        width: 350px;
        left: -130px;
        top: -130px;
      }

      &:first-child {
        @apply mt-0;
      }

      &:nth-child(even) {
        @screen md {
          @apply mr-0;
        }
      }

      &:nth-child(odd) {
        @screen md {
          @apply ml-0;
        }
      }

      &:nth-child(1) {
        @apply bg-blizzard-100;

        @screen md {
          @apply mt-0;
        }

        @screen lg {
          @apply ml-0 mr-5;
        }

        .icon {
          right: -16px;
          top: 55px;
        }

        &::before {
          background: radial-gradient(
            50% 50% at 50% 50%,
            rgba(72, 128, 163, 0.1) 0%,
            rgba(72, 128, 163, 0) 100%
          );
        }

        .title,
        p {
          @apply text-blizzard-600;
        }
      }

      &:nth-child(2) {
        @apply bg-sunburst-red-100;

        @screen md {
          @apply mt-0;
        }

        @screen lg {
          @apply mx-5;
        }

        &::before {
          background: radial-gradient(
            50% 50% at 50% 50%,
            rgba(241, 84, 30, 0.1) 0%,
            rgba(241, 84, 30, 0) 100%
          );
        }

        .icon {
          right: -17.49px;
          top: 13.05px;
          transform: rotate(2.45deg);
        }

        .title,
        p {
          @apply text-sunburst-red-700;
        }
      }

      &:nth-child(3) {
        @apply bg-live-red-100;

        @screen lg {
          @apply mx-5;
        }

        &::before {
          background: radial-gradient(
            50% 50% at 50% 50%,
            rgba(190, 58, 55, 0.1) 0%,
            rgba(190, 58, 55, 0) 100%
          );
        }

        .icon {
          right: -3.11px;
          top: -23.94px;
          transform: rotate(17.28deg);
        }

        .title,
        p {
          @apply text-live-red-600;
        }
      }

      &:nth-child(4) {
        @apply bg-winter-green-100;

        @screen lg {
          @apply ml-5 mr-0;
        }

        &::before {
          background: radial-gradient(
            50% 50% at 50% 50%,
            rgba(85, 141, 155, 0.1) 0%,
            rgba(85, 141, 155, 0) 100%
          );
        }

        .icon {
          right: -20.82px;
          top: 23px;
        }

        .title,
        p {
          @apply text-winter-green-600;
        }
      }

      .icon {
        @apply absolute rounded-none z-10;
      }

      .title,
      p {
        @apply z-20;
      }

      .title {
        @apply font-medium text-3xl text-live-red-600;
        line-height: 42px;

        @screen md {
          @apply text-4xl leading-13;
        }

        .inner {
          @apply w-auto !important;

          .numbers {
            @apply overflow-hidden;
            height: 42px;

            @screen md {
              height: 52px;
            }

            .slider {
              @apply flex-col w-auto !important;

              .number {
                @apply self-start w-auto !important;
              }
            }
          }

          .character {
            @apply w-auto !important;
          }
        }
      }

      .title + p {
        @apply font-normal mt-1 text-winter-green-900 text-origin leading-7 !important;
        max-width: calc(100% - 62px);

        @screen md {
          max-width: calc(100% - 86px);
        }

        @screen lg {
          @apply max-w-full;
        }
      }
    }
  }

  .reviews {
    @apply flex-col flex-wrap mt-10;

    @screen xl {
      @apply flex-row;
    }

    .review-g2 {
      @screen xl {
        @apply w-span7 ml-0 mr-5;
      }

      :deep(.tag) {
        @apply bg-livestorm-blue-100 text-livestorm-blue-700;
      }
    }

    .testimonials {
      @apply mt-10;

      @screen md {
        @apply mt-12;
      }

      @screen xl {
        @apply w-span5 ml-5 mr-0 mt-0;
      }
    }
  }

  .get-started {
    @apply mt-26 overflow-hidden;

    @screen md {
      @apply mt-46;
    }
  }

  .seo-items {
    @apply mt-26;

    @screen md {
      @apply mt-46;
    }

    .list {
      @apply flex-col;

      .item {
        @apply flex-col mt-6;

        &:first-child {
          @apply mt-0;
        }

        :deep(.text) {
          @apply mt-1 text-grey-blue-600 text-base leading-[26px];

          a {
            @apply text-livestorm-blue-700;
          }

          b,
          strong {
            @apply font-medium text-grey-blue-600;
          }

          ol {
            li {
              @apply font-normal text-grey-blue-600 text-base leading-[26px] list-decimal list-inside;
            }
          }

          ul {
            li {
              @apply block font-normal text-grey-blue-600 text-base leading-[26px];

              &:before {
                @apply inline-block w-3 h-3 bg-livestorm-blue-700 mr-3;
                border-radius: 3px;
                content: "";
              }
            }
          }

          p + p,
          p + ol,
          p + ul,
          ol + ol,
          ol + p,
          ul + ul,
          ul + p {
            @apply mt-1;
          }
        }

        .image {
          @apply justify-center mt-6;

          .inner {
            @apply relative max-w-[840px] bg-grey-blue-100 rounded-4xl overflow-hidden;
          }
        }

        .video {
          @apply justify-center mt-6;

          .inner {
            @apply relative max-w-[840px] bg-grey-blue-100 aspect-video rounded-4xl overflow-hidden;

            iframe {
              @apply absolute h-full w-full flex;
            }
          }
        }
      }
    }
  }
}
</style>
